import { Assert, DeeplyBranded } from '@cotera/utilities';
import {
  ArrayType,
  EnumType,
  ExtendedAttributeType,
  PrimitiveAttributeType,
  PrimitiveType,
  RangeType,
  RecordType,
  StructType,
} from './ty';
import _ from 'lodash';

export type ImmutableTy = DeeplyBranded<ExtendedAttributeType, 'immutable'>;

const primitiveTyNn = (
  primitiveAttributeType: PrimitiveAttributeType
): ImmutableTy => {
  const ty = Object.freeze<PrimitiveType>({
    k: 'primitive',
    t: primitiveAttributeType,
  });

  const ety = Object.freeze<ExtendedAttributeType>({
    ty,
    nullable: false,
    tags: [],
  });

  return ety as ImmutableTy;
};

const rangeTyNn = (start: number, end: number): ImmutableTy => {
  const ty = Object.freeze<RangeType>({
    k: 'range',
    start,
    end,
    t: 'int',
  });

  const ety = Object.freeze<ExtendedAttributeType>({
    ty,
    nullable: false,
    tags: [],
  });

  return ety as ImmutableTy;
};

const enumTyNn = (variants: readonly string[]): ImmutableTy => {
  const ty = Object.freeze<EnumType>({
    k: 'enum',
    t: variants,
  });

  const ety = Object.freeze<ExtendedAttributeType>({
    ty,
    nullable: false,
    tags: [],
  });

  return ety as ImmutableTy;
};

const arrayTyNn = (eltTy: ImmutableTy): ImmutableTy => {
  const ty = Object.freeze<ArrayType>({
    k: 'array',
    t: eltTy,
  });

  const ety = Object.freeze<ExtendedAttributeType>({
    ty,
    nullable: false,
    tags: [],
  });

  return ety as ImmutableTy;
};

const recordTyNn = (valueTy: ImmutableTy): ImmutableTy => {
  const ty = Object.freeze<RecordType>({
    k: 'record',
    t: valueTy,
  });

  const ety = Object.freeze<ExtendedAttributeType>({
    ty,
    nullable: false,
    tags: [],
  });

  return ety as ImmutableTy;
};

const structTyNn = (fields: Record<string, ImmutableTy>): ImmutableTy => {
  const ty = Object.freeze<StructType>({
    k: 'struct',
    fields,
  });

  const ety = Object.freeze<ExtendedAttributeType>({
    ty,
    nullable: false,
    tags: [],
  });

  return ety as ImmutableTy;
};

const nullable = (ty: ImmutableTy): ImmutableTy => {
  const ety = Object.freeze<ExtendedAttributeType>({
    ty: ty.ty,
    nullable: true,
    tags: [],
  });

  return ety as ImmutableTy;
};

const copy = (ty: ImmutableTy): ImmutableTy => {
  const ety = Object.freeze<ExtendedAttributeType>({
    ty: ty.ty,
    nullable: ty.nullable,
    tags: ty.tags,
  });

  return ety as ImmutableTy;
};

const derive = (ty: ExtendedAttributeType): ImmutableTy => {
  const baseTy = (() => {
    switch (ty.ty.k) {
      case 'primitive':
        return primitiveTyNn(ty.ty.t);

      case 'range':
        return rangeTyNn(ty.ty.start, ty.ty.end);

      case 'enum':
        return enumTyNn(ty.ty.t);

      case 'array':
        return arrayTyNn(derive(ty.ty.t));

      case 'record':
        return recordTyNn(derive(ty.ty.t));

      case 'struct':
        return structTyNn(_.mapValues(ty.ty.fields, derive));

      default:
        return Assert.unreachable(ty.ty);
    }
  })();

  return ty.nullable ? nullable(baseTy) : baseTy;
};

export const CreateImmutable = {
  primitiveTyNn,
  rangeTyNn,
  enumTyNn,
  arrayTyNn,
  recordTyNn,
  structTyNn,
  nullable,
  copy,
  derive,
};

Object.freeze(CreateImmutable);
